import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, FileText, Truck, Clock, TrendingUp, CheckCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePurchaseOrders } from '@/hooks/use-purchase-orders';
import {
  useAvailableSourcingEvents, useSupplierBids 
} from '@/hooks/use-sourcing';
import {
  PO_STATUS_LABELS, PO_STATUS_COLORS 
} from '@/types/orders';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: posData } = usePurchaseOrders();
  const { data: eventsData } = useAvailableSourcingEvents();
  const { data: bidsData } = useSupplierBids();

  const orders = posData?.data ?? [];
  const events = eventsData?.data ?? [];
  const bids = bidsData?.data ?? [];

  const openOrders = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
  const pendingAck = orders.filter((o) => o.status === 'sent');
  const submittedBids = bids.filter((b) => b.status === 'submitted' || b.status === 'under_review');
  const totalOrderValue = openOrders.reduce((sum, o) => sum + o.total, 0);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD' 
    }).format(amount);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Supplier Dashboard</h1>
        <p className="text-muted-foreground">Welcome back. Here is your activity overview.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard 
          title="Open Sourcing Events" 
          value={String(events.length)} 
          change="Available to bid" 
          icon={ShoppingCart} 
        />
        <KpiCard 
          title="Active Bids" 
          value={String(submittedBids.length)} 
          change={`${bids.filter((b) => b.status === 'accepted').length} accepted`}
          icon={FileText} 
        />
        <KpiCard 
          title="Open Orders" 
          value={String(openOrders.length)} 
          change={formatCurrency(totalOrderValue)}
          icon={Truck} 
        />
        <KpiCard 
          title="Pending Acknowledgment" 
          value={String(pendingAck.length)} 
          change="Requires action" 
          icon={CheckCircle} 
          variant={pendingAck.length > 0 ? 'warning' : 'default'}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Upcoming Deadlines
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/sourcing/available')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No open events</p>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 3).map((event) => (
                  <DeadlineItem 
                    key={event.eventId}
                    title={event.title} 
                    deadline={formatDeadline(event.bidDeadline)} 
                    value={formatCurrency(event.estimatedValue)} 
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Recent Orders
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/orders/purchase-orders')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No orders yet</p>
            ) : (
              <div className="space-y-3">
                {orders.slice(0, 4).map((order) => (
                  <div key={order.poId} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                    <div>
                      <p className="font-medium">{order.poNumber}</p>
                      <p className="text-sm text-muted-foreground">{order.deliverySiteName}</p>
                    </div>
                    <div className="text-right">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PO_STATUS_COLORS[order.status]}`}>
                        {PO_STATUS_LABELS[order.status]}
                      </span>
                      <p className="text-sm font-medium mt-1">{formatCurrency(order.total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-green-600">96.2%</p>
              <p className="text-sm text-muted-foreground">On-Time Delivery</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-blue-600">98.5%</p>
              <p className="text-sm text-muted-foreground">Quality Score</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-purple-600">4.8/5</p>
              <p className="text-sm text-muted-foreground">Overall Rating</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function formatDeadline(dateStr: string): string {
  const deadline = new Date(dateStr);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 0) return 'Expired';
  if (diffHours < 24) return `${diffHours}h remaining`;
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `${diffDays} days`;
  return deadline.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric' 
  });
}

interface KpiCardProps {
  readonly title: string;
  readonly value: string;
  readonly change: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly variant?: 'default' | 'success' | 'warning';
}

function KpiCard({
  title, value, change, icon, variant = 'default' 
}: KpiCardProps) {
  const IconComponent = icon;
  const iconColors = {
    default: 'text-muted-foreground',
    success: 'text-green-500',
    warning: 'text-amber-500',
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <IconComponent className={`h-5 w-5 ${iconColors[variant]}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{change}</p>
      </CardContent>
    </Card>
  );
}

interface DeadlineItemProps {
  readonly title: string;
  readonly deadline: string;
  readonly value: string;
}

function DeadlineItem({
  title, deadline, value 
}: DeadlineItemProps) {
  return (
    <div className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{deadline}</p>
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
